// Port of webapp/src/octoClient.ts, trimmed to the endpoints the cloned
// surface actually calls. Every path below is the real `/api/v2/...` route the
// Go server exposes; in this build they are answered by src/mocks/handlers.ts.
//
// Difference from upstream: upstream returns the raw `Response` for mutations
// and lets `mutator.ts` decide what to do; here mutations resolve to booleans
// and the store slices own the optimistic update.

import {Block, BlockPatch} from '../types/block'
import {Board, BoardPatch} from '../types/board'
import {CategoryBoards} from '../types/category'
import {ClientConfig, defaultClientConfig, IUser} from '../types/user'
import {Constants} from '../common/constants'

export const SESSION_STORAGE_KEY = 'focalboardSessionId'

export type Team = {
    id: string
    title: string
    description?: string
}

export type SearchBoardsResult = {
    boards: Board[]
    total: number
}

class OctoClient {
    private logged = false

    constructor(public teamId: string = Constants.globalTeamId, public channelId: string = Constants.noChannelID) {}

    private getBaseURL(): string {
        const baseURL = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')
        if (!this.logged) {
            console.info(`[mock-api] base URL: ${baseURL || '(same origin)'}`)
            this.logged = true
        }
        return baseURL
    }

    get token(): string {
        return localStorage.getItem(SESSION_STORAGE_KEY) || ''
    }

    set token(value: string) {
        localStorage.setItem(SESSION_STORAGE_KEY, value)
    }

    private headers(): Record<string, string> {
        return {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: this.token ? 'Bearer ' + this.token : '',
            'X-Requested-With': 'XMLHttpRequest',
        }
    }

    private teamPath(teamId?: string): string {
        return `/api/v2/teams/${teamId || this.teamId}`
    }

    private url(path: string): string {
        return this.getBaseURL() + path
    }

    private async getJson<T>(response: Response, defaultValue: T): Promise<T> {
        try {
            const value = await response.json()
            return value || defaultValue
        } catch {
            return defaultValue
        }
    }

    private async ok(response: Response): Promise<boolean> {
        return response.status === 200
    }

    /** Keeps the same teamId/channelId instance semantics as upstream. */
    setTeamId(teamId: string, channelId = Constants.noChannelID): void {
        this.teamId = teamId
        this.channelId = channelId
    }

    // ---- auth -------------------------------------------------------------

    async login(username: string, password: string): Promise<boolean> {
        const response = await fetch(this.url('/api/v2/login'), {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({username, password, type: 'normal'}),
        })
        if (response.status !== 200) {
            return false
        }
        const json = await this.getJson<{ token?: string }>(response, {})
        if (json.token) {
            this.token = json.token
            return true
        }
        return false
    }

    async logout(): Promise<boolean> {
        const response = await fetch(this.url('/api/v2/logout'), {method: 'POST', headers: this.headers()})
        localStorage.removeItem(SESSION_STORAGE_KEY)
        return this.ok(response)
    }

    async register(email: string, username: string, password: string, token?: string): Promise<{code: number, json: {error?: string}}> {
        const response = await fetch(this.url('/api/v2/register'), {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({email, username, password, token}),
        })
        return {code: response.status, json: await this.getJson<{ error?: string }>(response, {})}
    }

    async getMe(): Promise<IUser | undefined> {
        const params: string[] = []
        if (this.teamId !== Constants.globalTeamId) {
            params.push(`teamID=${encodeURIComponent(this.teamId)}`)
        }
        if (this.channelId !== Constants.noChannelID) {
            params.push(`channelID=${encodeURIComponent(this.channelId)}`)
        }
        const query = params.length ? `?${params.join('&')}` : ''
        const response = await fetch(this.url(`/api/v2/users/me${query}`), {headers: this.headers()})
        if (response.status !== 200) {
            return undefined
        }
        return this.getJson<IUser>(response, {} as IUser)
    }

    async getClientConfig(): Promise<ClientConfig | null> {
        const response = await fetch(this.url('/api/v2/clientConfig'), {headers: this.headers()})
        if (response.status !== 200) {
            return null
        }
        return this.getJson<ClientConfig>(response, defaultClientConfig)
    }

    // ---- teams / boards ---------------------------------------------------

    async getTeams(): Promise<Team[]> {
        const response = await fetch(this.url('/api/v2/teams'), {headers: this.headers()})
        if (response.status !== 200) {
            return []
        }
        return this.getJson<Team[]>(response, [])
    }

    async getBoards(teamId?: string): Promise<Board[]> {
        return this.getBoardsWithPath(this.teamPath(teamId) + '/boards')
    }

    async getTemplates(teamId?: string): Promise<Board[]> {
        return this.getBoardsWithPath(this.teamPath(teamId) + '/templates')
    }

    private async getBoardsWithPath(path: string): Promise<Board[]> {
        const response = await fetch(this.url(path), {headers: this.headers()})
        if (response.status !== 200) {
            return []
        }
        return this.getJson<Board[]>(response, [])
    }

    async getBoard(boardId: string): Promise<Board | undefined> {
        const response = await fetch(this.url(`/api/v2/boards/${encodeURIComponent(boardId)}`), {
            method: 'GET',
            headers: this.headers(),
        })
        if (response.status !== 200) {
            return undefined
        }
        return this.getJson<Board>(response, {} as Board)
    }

    async createBoard(board: Board, teamId?: string): Promise<Board> {
        const response = await fetch(this.url(this.teamPath(teamId || board.teamId) + '/boards'), {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(board),
        })
        return this.getJson<Board>(response, board)
    }

    async patchBoard(boardId: string, patch: BoardPatch): Promise<boolean> {
        const response = await fetch(this.url(`/api/v2/boards/${encodeURIComponent(boardId)}`), {
            method: 'PATCH',
            headers: this.headers(),
            body: JSON.stringify(patch),
        })
        return this.ok(response)
    }

    async patchBoards(boardIds: string[], patches: BoardPatch[]): Promise<boolean> {
        const response = await fetch(this.url(this.teamPath() + '/boards'), {
            method: 'PATCH',
            headers: this.headers(),
            body: JSON.stringify({board_ids: boardIds, board_patches: patches}),
        })
        return this.ok(response)
    }

    async deleteBoard(boardId: string): Promise<boolean> {
        const response = await fetch(this.url(`/api/v2/boards/${encodeURIComponent(boardId)}`), {
            method: 'DELETE',
            headers: this.headers(),
        })
        return this.ok(response)
    }

    async searchBoards(term: string): Promise<Board[]> {
        const response = await fetch(this.url(`/api/v2/boards/search?q=${encodeURIComponent(term)}`), {
            method: 'POST',
            headers: this.headers(),
            body: '{}',
        })
        if (response.status !== 200) {
            return []
        }
        const json = await this.getJson<Partial<SearchBoardsResult>>(response, {})
        return json.boards || []
    }

    // ---- blocks -----------------------------------------------------------

    async getBlocksWithParent(parentId: string, type?: string, teamId?: string): Promise<Block[]> {
        let path: string
        if (type) {
            path = this.teamPath(teamId) + `/blocks?parent_id=${encodeURIComponent(parentId)}&type=${encodeURIComponent(type)}`
        } else {
            path = this.teamPath(teamId) + `/blocks?parent_id=${encodeURIComponent(parentId)}`
        }
        return this.getBlocksWithPath(path)
    }

    async getBlocksWithType(type: string, teamId?: string): Promise<Block[]> {
        return this.getBlocksWithPath(this.teamPath(teamId) + `/blocks?type=${encodeURIComponent(type)}`)
    }

    async getBlocksWithBlockId(blockId: string, boardId: string): Promise<Block[]> {
        return this.getBlocksWithPath(`/api/v2/boards/${encodeURIComponent(boardId)}/blocks?block_id=${encodeURIComponent(blockId)}`)
    }

    /** `?all=true` also returns archived blocks, like upstream getAllBlocks(). */
    async getAllBlocks(boardId: string): Promise<Block[]> {
        return this.getBlocksWithPath(`/api/v2/boards/${encodeURIComponent(boardId)}/blocks?all=true`)
    }

    private async getBlocksWithPath(path: string): Promise<Block[]> {
        const response = await fetch(this.url(path), {headers: this.headers()})
        if (response.status !== 200) {
            return []
        }
        return this.getJson<Block[]>(response, [])
    }

    async insertBlock(block: Block, teamId?: string): Promise<Block> {
        const response = await fetch(this.url(this.teamPath(teamId || this.teamId) + '/blocks'), {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(block),
        })
        return this.getJson<Block>(response, block)
    }

    async patchBlock(boardId: string, blockId: string, patch: BlockPatch): Promise<boolean> {
        const response = await fetch(this.url(`/api/v2/boards/${boardId}/blocks/${blockId}`), {
            method: 'PATCH',
            headers: this.headers(),
            body: JSON.stringify(patch),
        })
        return this.ok(response)
    }

    async patchBlocks(blocks: Block[], patches: BlockPatch[], teamId?: string): Promise<boolean> {
        const response = await fetch(this.url(this.teamPath(teamId) + '/blocks'), {
            method: 'PATCH',
            headers: this.headers(),
            body: JSON.stringify({
                block_ids: blocks.map((b) => b.id),
                block_patches: patches,
            }),
        })
        return this.ok(response)
    }

    async deleteBlock(boardId: string, blockId: string): Promise<boolean> {
        const response = await fetch(this.url(`/api/v2/boards/${boardId}/blocks/${encodeURIComponent(blockId)}`), {
            method: 'DELETE',
            headers: this.headers(),
        })
        return this.ok(response)
    }

    async undeleteBlock(boardId: string, blockId: string): Promise<boolean> {
        const response = await fetch(
            this.url(`/api/v2/boards/${encodeURIComponent(boardId)}/blocks/${encodeURIComponent(blockId)}/undelete`),
            {method: 'POST', headers: this.headers()},
        )
        return this.ok(response)
    }

    // ---- users ------------------------------------------------------------

    /** GET /api/v2/teams/:teamId/users — team members, optionally filtered. */
    async getUsers(term?: string, teamId?: string): Promise<IUser[]> {
        let path = this.teamPath(teamId) + '/users'
        if (term) {
            path += `?search=${encodeURIComponent(term)}`
        }
        const response = await fetch(this.url(path), {headers: this.headers()})
        if (response.status !== 200) {
            return []
        }
        return this.getJson<IUser[]>(response, [])
    }

    // ---- sidebar categories ----------------------------------------------

    async getSidebarCategories(teamId: string): Promise<CategoryBoards[]> {
        const response = await fetch(this.url(`/api/v2/teams/${encodeURIComponent(teamId)}/categories`), {headers: this.headers()})
        if (response.status !== 200) {
            return []
        }
        return this.getJson<CategoryBoards[]>(response, [])
    }

    async patchSidebarCategory(teamId: string, category: CategoryBoards): Promise<CategoryBoards | undefined> {
        const response = await fetch(
            this.url(`/api/v2/teams/${encodeURIComponent(teamId)}/categories/${encodeURIComponent(category.id)}`),
            {method: 'PATCH', headers: this.headers(), body: JSON.stringify(category)},
        )
        if (response.status !== 200) {
            return undefined
        }
        return this.getJson<CategoryBoards>(response, category)
    }

    // ---- clone-only helpers (no upstream equivalent) ----------------------

    /** Powers the "重置演示数据" entry in the user menu. */
    async resetMockDatabase(): Promise<boolean> {
        const response = await fetch(this.url('/api/v2/mock/reset'), {method: 'POST', headers: this.headers()})
        return this.ok(response)
    }
}

const client = new OctoClient()
export default client
export {OctoClient}
